import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { catchError, Observable, of, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
  // List of URL patterns that should NOT trigger redirect on 401
  // These are supplementary/optional data endpoints
  private readonly optionalEndpoints = [
    '/events/by-compilation/',  // Events for a compilation (trip)
    '/events/by-quest/',        // Events for a quest
  ];

  constructor(private router: Router, private authService: AuthService) {}

  /**
   * Check if the request URL is for an optional endpoint
   * that shouldn't trigger redirect on 401
   */
  private isOptionalEndpoint(url: string): boolean {
    return this.optionalEndpoints.some(pattern => url.includes(pattern));
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip auth for certain endpoints
    if (
      (req.url.includes('auth') && !req.url.includes('password/change') && !req.url.includes('current-user')) ||
      req.url.includes('assets') || req.url.includes('api.openai') || req.url.includes('password/change/confirm')
      || req.url.includes('mapbox')
    ) {
      return next.handle(req);
    }

    let preObs$;

    //if more than 1 hr passed since the last token refresh - refresh it again
    if (Date.now() - this.authService.lastTokenUpdate.getTime() > 3600000) {
      preObs$ = this.authService.refreshToken();
    } else {
      preObs$ = of(true);
    }

    // @ts-ignore
    return preObs$.pipe(
      // @ts-ignore
      switchMap(() => {
        const value = localStorage.getItem('accessToken');

        if (value) {
          // User has token, attach it to request
          req = req.clone({ setHeaders: { Authorization: `Bearer ${value}` } });

          return next.handle(req).pipe(
            catchError((err: HttpErrorResponse) => {
              if (err.status === 401) {
                // Don't redirect for optional endpoints - just fail silently
                if (!this.isOptionalEndpoint(req.url)) {
                  // Store the current URL for redirect after login
                  const currentUrl = this.router.url;
                  localStorage.setItem('redirectUrl', currentUrl);
                  this.router.navigate(['/auth/login']);
                }
              }

              return throwError(() => err);
            })
          );
        } else {
          // No token - try the request anyway (for public endpoints)
          return next.handle(req).pipe(
            catchError((err: HttpErrorResponse) => {
              if (err.status === 401) {
                // Don't redirect for optional endpoints - just fail silently
                if (!this.isOptionalEndpoint(req.url)) {
                  // Store the current URL for redirect after login
                  const currentUrl = this.router.url;
                  localStorage.setItem('redirectUrl', currentUrl);
                  this.router.navigate(['/auth/login']);
                }
              }

              return throwError(() => err);
            })
          );
        }
      })
    );
  }
}
